// TanStack Query hooks for the deal-flow API surface.
//
// Read hooks render skeletons via isLoading; mutation hooks invalidate the
// relevant query keys on success and surface backend validation errors via
// the existing <Toast> primitive in the calling component.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import {
  assignmentsApi,
  catalogApi,
  certificationsApi,
  certificationTemplatesApi,
  checklistApi,
  closingApi,
  dealFlowOverviewApi,
  dealOutcomeApi,
  dealPaymentSetupApi,
  dealTpsAssignmentApi,
  escrowApi,
  failuresApi,
  finalFinancialAddendumApi,
  laneAssessmentsApi,
  loanDocumentIssuanceApi,
} from "@/api/dealFlow/client";
// Aliased — this is a DIFFERENT escrowApi (the API-driven EscrowTransaction
// module) than the one imported above (the legacy per-deal EscrowSetup).
import { escrowApi as escrowTransactionsApi } from "@/api/escrow";
import { ApiError } from "@/api/client";
import type {
  CertifyEscrowFundingVerifiedRequest,
  CertifyEscrowIssuedRequest,
  CreateCatalogItemRequest,
  CreateCertificationTemplateRequest,
  CriteriaCatalogItemDto,
  CriteriaChecklistItemDto,
  CriteriaLane,
  CriteriaTier,
  DealAssignmentDto,
  DealPaymentSetupDto,
  DealPaymentSetupParty,
  DealPaymentSetupPartyDto,
  DealTpsAssignment,
  DealTpsOption,
  SetDealTpsRequest,
  DealFlowTab,
  DealOutcomeDto,
  DeclineChecklistItemRequest,
  EscrowSetupDto,
  FailureListQuery,
  FinalFinancialAddendumDto,
  IssueLaneAssessmentRequest,
  LaneAssessmentDto,
  LoanCreationResultDto,
  LoanDocumentIssuanceDto,
  CertifyCertificationRequest,
  DeclineCertificationRequest,
  EscalateCertificationRequest,
  ReleaseEscrowRequest,
  ResolveFailureRequest,
  SaveCertificationStateRequest,
  SignCertificationRequest,
  UnderwritingCertificationDto,
  UnderwritingCertificationTemplateDto,
  UpdateCatalogItemRequest,
  UpdateCertificationTemplateRequest,
  UpdateFailureRequest,
  UpsertAssignmentRequest,
  UpsertEscrowSetupRequest,
  UpsertFinalFinancialAddendumRequest,
  UpsertLoanDocumentIssuanceRequest,
  WaiveChecklistItemRequest,
} from "@/api/dealFlow/types";
// Leaf import (not the barrel) — avoids a cycle through the components that
// consume this hook module.
import { tabForAnalystLane } from "@/components/DealFlow/AdditionalCertifications/certConfigs";
import { escalationKeys } from "./escalationKeys";

// ============================== query keys ============================

export const dealFlowKeys = {
  all: ["deal-flow"] as const,
  catalog: (lane?: CriteriaLane, tier?: CriteriaTier) =>
    ["deal-flow", "catalog", lane ?? "any", tier ?? "any"] as const,
  assignments: (dealId: string) =>
    ["deal-flow", "assignments", dealId] as const,
  checklist: (dealId: string, lane: CriteriaLane) =>
    ["deal-flow", "checklist", dealId, lane] as const,
  checklistItem: (dealId: string, itemId: string) =>
    ["deal-flow", "checklist-item", dealId, itemId] as const,
  laneAssessments: (dealId: string) =>
    ["deal-flow", "lane-assessments", dealId] as const,
  laneAssessment: (dealId: string, lane: CriteriaLane) =>
    ["deal-flow", "lane-assessment", dealId, lane] as const,
  outcome: (dealId: string) => ["deal-flow", "outcome", dealId] as const,
  escrow: (dealId: string) => ["deal-flow", "escrow", dealId] as const,
  // Under the escrow prefix, so every legacy escrow mutation (which
  // invalidates `escrow(dealId)`) refreshes the shared status too.
  escrowStatus: (dealId: string) => ["deal-flow", "escrow", dealId, "status"] as const,
  certifications: (dealId: string, tab: DealFlowTab, section: number) =>
    ["deal-flow", "certifications", dealId, tab, section] as const,
  certTemplates: (tab: DealFlowTab) =>
    ["deal-flow", "cert-templates", "by-tab", tab] as const,
  loanDocumentIssuance: (dealId: string) =>
    ["deal-flow", "loan-document-issuance", dealId] as const,
  finalFinancialAddendum: (dealId: string) =>
    ["deal-flow", "final-financial-addendum", dealId] as const,
  failures: (q?: FailureListQuery) =>
    ["deal-flow", "failures", q ?? {}] as const,
  paymentSetup: (dealId: string) =>
    ["deal-flow", "payment-setup", dealId] as const,
  /** Portal-account picker inside the Step 2 re-link modal. */
  paymentSetupAccountPicker: (party: DealPaymentSetupParty, search: string) =>
    ["deal-flow", "payment-setup", "account-picker", party, search] as const,
  /** Closing Step 2 — the staff-driven servicer (TPS) decision + its options. */
  tpsAssignment: (dealId: string) =>
    ["deal-flow", "tps-assignment", dealId] as const,
  tpsAssignmentOptions: (dealId: string) =>
    ["deal-flow", "tps-assignment-options", dealId] as const,
  createLoanPreview: (dealId: string) =>
    ["deal-flow", "create-loan-preview", dealId] as const,
  // Shares the "escrow" prefix with api/escrow.ts's own callers
  // (CreateEscrowModal, the standalone /escrow pages), so CreateEscrowModal's
  // prefix invalidation on draft-create also covers these new-model Section 2
  // / Closing-gate reads.
  escrowTransactionsForDeal: (dealId: string | undefined) =>
    ["escrow", "list", { dealId }] as const,
  escrowTransactionDetail: (id: string | undefined) =>
    ["escrow", "detail", id] as const,
  // Closing-gate Bypass. The prefix covers both the per-deal and the
  // cross-deal recent reads so a bypass mutation can invalidate them together.
  gateBypass: ["deal-flow", "gate-bypass"] as const,
  gateBypassForDeal: (dealId: string) =>
    ["deal-flow", "gate-bypass", "by-deal", dealId] as const,
  gateBypassRecent: (take: number) =>
    ["deal-flow", "gate-bypass", "recent", take] as const,
};

// Invalidate every cache that depends on a deal — used after stage-advancing
// mutations like hand-off-to-closing.
function invalidateDealCaches(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
) {
  qc.invalidateQueries({ queryKey: ["deal-flow", "assignments", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "checklist", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "lane-assessments", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "outcome", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "escrow", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "certifications", dealId] });
  qc.invalidateQueries({ queryKey: ["deal-flow", "loan-document-issuance", dealId] });
  qc.invalidateQueries({
    queryKey: ["deal-flow", "final-financial-addendum", dealId],
  });
  // The deal-summary tab strip reads stage / sub-state — refresh it so
  // hand-off-to-closing flips the Closing tab placeholder.
  qc.invalidateQueries({ queryKey: ["deals", "detail", dealId] });
}

// ============================== catalog ===============================

/** Read-only catalog query (cached for the session by default). */
export function useCriteriaCatalog(
  lane?: CriteriaLane,
  tier?: CriteriaTier,
  isActive = true,
) {
  return useQuery<CriteriaCatalogItemDto[]>({
    queryKey: dealFlowKeys.catalog(lane, tier),
    queryFn: () => catalogApi.list(lane, tier, isActive),
    // Catalog rows are seeded on deploy; cache aggressively.
    staleTime: 10 * 60 * 1000,
  });
}

/** ADMIN-only catalog template edit. Invalidates every cached lane/tier
 *  variant of the catalog query so open checklist surfaces re-render with the
 *  new copy — including in-progress deals (instances FK by GUID, no snapshot). */
export function useUpdateCatalogItem() {
  const qc = useQueryClient();
  return useMutation<
    CriteriaCatalogItemDto,
    unknown,
    { id: string; body: UpdateCatalogItemRequest }
  >({
    mutationFn: ({ id, body }) => catalogApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "catalog"] });
    },
  });
}

/** ADMIN-only catalog add. The server generates the code from lane + tier.
 *  Invalidates every cached lane/tier variant so the new item appears on every
 *  open checklist surface (and on in-progress deals via the lazy-create path). */
export function useCreateCatalogItem() {
  const qc = useQueryClient();
  return useMutation<CriteriaCatalogItemDto, unknown, CreateCatalogItemRequest>({
    mutationFn: (body) => catalogApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "catalog"] });
    },
  });
}

// ===================== certification templates =======================

/** Section 2 certification templates for an analyst lane (A2/A3/A4). The DB
 *  template list is the single source of truth for the cert universe — it
 *  drives both the certification rows and useAnalystTabState's Section-3 gate.
 *  Seeded on deploy; cache aggressively. */
export function useCertificationTemplatesByLane(lane: CriteriaLane) {
  const tab = tabForAnalystLane(lane);
  return useQuery<UnderwritingCertificationTemplateDto[]>({
    queryKey: dealFlowKeys.certTemplates(tab),
    queryFn: () => certificationTemplatesApi.listByTab(tab),
    staleTime: 10 * 60 * 1000,
  });
}

/** ADMIN-only certification-template edit. Invalidates the by-tab list (so the
 *  certification rows re-render with the new title/blurb) AND the per-code
 *  modal query (so an open OPEN-FORM modal shows the new prose) — including
 *  in-progress deals, which reference the template by code with no snapshot. */
export function useUpdateCertificationTemplate() {
  const qc = useQueryClient();
  return useMutation<
    UnderwritingCertificationTemplateDto,
    unknown,
    { certificationCode: string; body: UpdateCertificationTemplateRequest }
  >({
    mutationFn: ({ certificationCode, body }) =>
      certificationTemplatesApi.update(certificationCode, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "cert-templates"] });
      qc.invalidateQueries({ queryKey: ["deal-flow", "cert-template"] });
    },
  });
}

/** ADMIN-only certification-template add. The server derives the full code from
 *  tab + key. Invalidates the by-tab list AND the per-code modal query so the
 *  new template appears on every deal's Section 2 list (and feeds the Section-3
 *  completion gate) on the next analyst-tab load. */
export function useCreateCertificationTemplate() {
  const qc = useQueryClient();
  return useMutation<
    UnderwritingCertificationTemplateDto,
    unknown,
    CreateCertificationTemplateRequest
  >({
    mutationFn: (body) => certificationTemplatesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "cert-templates"] });
      qc.invalidateQueries({ queryKey: ["deal-flow", "cert-template"] });
    },
  });
}

// ============================== assignments ===========================

/** List the 7 pills for a deal. */
export function useDealAssignments(dealId: string | undefined) {
  return useQuery<DealAssignmentDto[]>({
    queryKey: dealFlowKeys.assignments(dealId ?? ""),
    queryFn: () => assignmentsApi.list(dealId!),
    enabled: !!dealId,
  });
}

/** Upsert a single pill. Optimistic update + rollback on error. */
export function useUpdateAssignment(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roleCode,
      body,
    }: {
      roleCode: string;
      body: UpsertAssignmentRequest;
    }) => assignmentsApi.upsert(dealId, roleCode, body),
    onMutate: async ({ roleCode, body }) => {
      await qc.cancelQueries({
        queryKey: dealFlowKeys.assignments(dealId),
      });
      const prev = qc.getQueryData<DealAssignmentDto[]>(
        dealFlowKeys.assignments(dealId),
      );
      qc.setQueryData<DealAssignmentDto[]>(
        dealFlowKeys.assignments(dealId),
        (old) => {
          const rows = old ? [...old] : [];
          const idx = rows.findIndex((a) => a.roleCode === roleCode);
          const stub: DealAssignmentDto =
            idx >= 0
              ? { ...rows[idx], status: body.status, assignedUserId: body.assignedUserId }
              : {
                  id: `optimistic-${roleCode}`,
                  dealId,
                  roleCode,
                  status: body.status,
                  assignedUserId: body.assignedUserId,
                  assignedAt: null,
                  assignedByUserId: null,
                };
          if (idx >= 0) rows[idx] = stub;
          else rows.push(stub);
          return rows;
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(dealFlowKeys.assignments(dealId), ctx.prev);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealFlowKeys.assignments(dealId) });
    },
  });
}

/** Explicit Not-Applicable POST (some callers prefer the
 *  semantic endpoint over UpsertAssignment with status=NotApplicable). */
export function useMarkSlotNotApplicable(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleCode: string) => assignmentsApi.markNa(dealId, roleCode),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealFlowKeys.assignments(dealId) });
    },
  });
}

// ============================== checklist =============================

export function useCriteriaChecklistByLane(
  dealId: string | undefined,
  lane: CriteriaLane,
) {
  return useQuery<CriteriaChecklistItemDto[]>({
    queryKey: dealFlowKeys.checklist(dealId ?? "", lane),
    queryFn: () => checklistApi.list(dealId!, lane),
    enabled: !!dealId,
  });
}

export function useChecklistItem(
  dealId: string | undefined,
  itemId: string | undefined,
) {
  return useQuery<CriteriaChecklistItemDto>({
    queryKey: dealFlowKeys.checklistItem(dealId ?? "", itemId ?? ""),
    queryFn: () => checklistApi.get(dealId!, itemId!),
    enabled: !!dealId && !!itemId,
  });
}

function invalidateChecklist(
  qc: ReturnType<typeof useQueryClient>,
  dealId: string,
) {
  qc.invalidateQueries({ queryKey: ["deal-flow", "checklist", dealId] });
}

export function useCertifyChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => checklistApi.certify(dealId, itemId),
    onSuccess: () => invalidateChecklist(qc, dealId),
  });
}

export function useEscalateChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, escalationReason }: { itemId: string; escalationReason: string }) =>
      checklistApi.escalate(dealId, itemId, escalationReason),
    onSuccess: () => {
      invalidateChecklist(qc, dealId);
      // A new EscalationRecord exists — refresh the Escalations queue.
      qc.invalidateQueries({ queryKey: escalationKeys.all });
    },
  });
}

export function useDeclineChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: DeclineChecklistItemRequest }) =>
      checklistApi.decline(dealId, itemId, body),
    onSuccess: () => invalidateChecklist(qc, dealId),
  });
}

export function useWaiveChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: WaiveChecklistItemRequest }) =>
      checklistApi.waive(dealId, itemId, body),
    onSuccess: () => invalidateChecklist(qc, dealId),
  });
}

export function useAdminResetChecklistItem(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, reason }: { itemId: string; reason: string }) =>
      checklistApi.reset(dealId, itemId, { reason }),
    onSuccess: () => invalidateChecklist(qc, dealId),
  });
}

// ============================== lane assessments ======================

export function useLaneAssessments(dealId: string | undefined) {
  return useQuery<LaneAssessmentDto[]>({
    queryKey: dealFlowKeys.laneAssessments(dealId ?? ""),
    queryFn: () => laneAssessmentsApi.list(dealId!),
    enabled: !!dealId,
  });
}

export function useLaneAssessment(
  dealId: string | undefined,
  lane: CriteriaLane,
) {
  return useQuery<LaneAssessmentDto | null>({
    queryKey: dealFlowKeys.laneAssessment(dealId ?? "", lane),
    queryFn: async () => {
      try {
        return await laneAssessmentsApi.get(dealId!, lane);
      } catch (err) {
        // 404 = lane not yet issued; surface as null rather than error.
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!dealId,
  });
}

export function useIssueLaneAssessment(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lane, body }: { lane: CriteriaLane; body: IssueLaneAssessmentRequest }) =>
      laneAssessmentsApi.issue(dealId, lane, body),
    onSuccess: (created, variables) => {
      // Prime the per-lane cache with the freshly created DTO so the
      // section flips from READY to ISSUED without waiting on a refetch
      // round-trip — this is the fix for the UI desync where
      // a 200 response left the section header stuck on READY.
      qc.setQueryData(
        dealFlowKeys.laneAssessment(dealId, variables.lane),
        created,
      );
      qc.invalidateQueries({
        queryKey: dealFlowKeys.laneAssessment(dealId, variables.lane),
      });
      qc.invalidateQueries({ queryKey: dealFlowKeys.laneAssessments(dealId) });
      qc.invalidateQueries({ queryKey: dealFlowKeys.outcome(dealId) });
    },
  });
}

// ============================== deal outcome ==========================

export function useDealOutcome(dealId: string | undefined) {
  return useQuery<DealOutcomeDto>({
    queryKey: dealFlowKeys.outcome(dealId ?? ""),
    queryFn: () => dealOutcomeApi.get(dealId!),
    enabled: !!dealId,
  });
}

/** A1 (or Admin) finalizes the DealOutcome, unlocking
 *  Section 8 cert signatures. Primes the outcome cache with the
 *  returned DTO so the Section 8 banner / FINALIZED chip / cert
 *  enable state flip without waiting on a refetch round-trip. */
export function useFinalizeDealOutcome(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dealOutcomeApi.finalize(dealId),
    onSuccess: (finalized) => {
      qc.setQueryData(dealFlowKeys.outcome(dealId), finalized);
      qc.invalidateQueries({ queryKey: dealFlowKeys.outcome(dealId) });
      // The cert-sign guard for Section 8 reads DealOutcome.finalized,
      // so refresh the Section 8 certifications list too.
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, "Underwriting", 8),
      });
    },
  });
}

// ============================== escrow ================================

export function useEscrowSetup(dealId: string | undefined) {
  return useQuery<EscrowSetupDto | null>({
    queryKey: dealFlowKeys.escrow(dealId ?? ""),
    queryFn: async () => {
      try {
        return await escrowApi.get(dealId!);
      } catch (err) {
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!dealId,
  });
}

export function useUpsertEscrowSetup(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertEscrowSetupRequest) => escrowApi.upsert(dealId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) }),
  });
}

export function useIssueEscrow(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CertifyEscrowIssuedRequest) => escrowApi.issue(dealId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) }),
  });
}

export function useVerifyEscrowFunding(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CertifyEscrowFundingVerifiedRequest) =>
      escrowApi.verifyFunding(dealId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) }),
  });
}

/** The shared escrow predicate (owner ruling 5): which model the deal uses, and
 *  whether it is funded and released. Closing Step 3 reads it. */
export function useDealEscrowStatus(dealId: string | undefined) {
  return useQuery({
    queryKey: dealFlowKeys.escrowStatus(dealId ?? ""),
    queryFn: () => escrowApi.status(dealId!),
    enabled: !!dealId,
  });
}

/** B2 releases escrow at the end of Closing Step 3. The
 *  server writes the matching Close.S3.EscrowReleased certification row
 *  atomically, so we invalidate both query keys. */
export function useReleaseEscrow(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReleaseEscrowRequest) => escrowApi.release(dealId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealFlowKeys.escrow(dealId) });
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, "Closing", 3),
      });
    },
  });
}

/**
 * New-model deals (no legacy EscrowSetup row) back Underwriting Section 2
 * with the real, API-driven Escrow.com transaction instead. At most one
 * ACTIVE (non-Cancelled, non-Archived) transaction can exist per deal
 * (UX_EscrowTransactions_DealId_Active), so this resolves "the" one, if any,
 * and fetches its full detail (including the server-derived lifecycle) in a
 * second round-trip. Pass `undefined` to disable both queries (e.g. while the
 * legacy-vs-new-model check is still loading, or for a legacy-model deal).
 */
export function useDealEscrowTransaction(dealId: string | undefined) {
  const list = useQuery({
    queryKey: dealFlowKeys.escrowTransactionsForDeal(dealId),
    queryFn: () => escrowTransactionsApi.list({ dealId, pageSize: 100 }),
    enabled: !!dealId,
  });

  // Server orders most-recent-first and (by default) excludes Archived; a
  // Cancelled row can still coexist with a later active one, so skip it.
  const listId = list.data?.data.find((x) => x.status !== "Cancelled")?.id;

  // The server's shared escrow predicate also counts an Archived transaction
  // that carries a release (owner ruling, 2026-10-09) — the 7-day sweep can
  // archive it after B2 recorded the release. The list above never returns it,
  // so fall back to the transaction the status names.
  const status = useDealEscrowStatus(dealId);
  const statusId =
    status.data?.model === "EscrowCom" ? (status.data.escrowTransactionId ?? undefined) : undefined;
  const activeId = listId ?? (list.isLoading ? undefined : statusId);

  const detail = useQuery({
    queryKey: dealFlowKeys.escrowTransactionDetail(activeId),
    queryFn: () => escrowTransactionsApi.get(activeId!),
    enabled: !!activeId,
  });

  return {
    isLoading:
      list.isLoading || (!listId && status.isLoading) || (!!activeId && detail.isLoading),
    transaction: detail.data ?? null,
  };
}

// ============================== certifications ========================

export function useUnderwritingCertifications(
  dealId: string | undefined,
  tab: DealFlowTab,
  section: number,
) {
  return useQuery<UnderwritingCertificationDto[]>({
    queryKey: dealFlowKeys.certifications(dealId ?? "", tab, section),
    queryFn: () => certificationsApi.list(dealId!, tab, section),
    enabled: !!dealId,
  });
}

export function usePostUnderwritingCertification(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SignCertificationRequest) =>
      certificationsApi.sign(dealId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
      });
      // Section-9 hand-off advances Deal.Stage atomically — invalidate broadly.
      if (
        variables.tab === "Underwriting" &&
        variables.section === 9 &&
        variables.certificationCode === "UW.S9.HandOffToClosing"
      ) {
        invalidateDealCaches(qc, dealId);
      }
    },
  });
}

// ---- Section-2 certification lifecycle mutations ------
//
// These mirror the Section-1 criteria-checklist certify/decline/escalate
// hooks: a thin mutation that invalidates the underwriting-certifications
// query key on success. The server refuses a repeat or out-of-turn
// transition with 409: a Certified row, an already-escalated row, or an
// escalation still pending or denied (owner ruling 15, 2026-10-08; owner
// approval, 2026-10-13). On a 409 these hooks still invalidate, so the row
// refetches its current state; the caller shows the server's message.

function isConflict(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "status" in err &&
    (err as { status: number }).status === 409
  );
}

export function useCertifyCertification(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CertifyCertificationRequest) =>
      certificationsApi.certify(dealId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
      });
    },
    onError: (err, variables) => {
      if (isConflict(err)) {
        qc.invalidateQueries({
          queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
        });
      }
    },
  });
}

export function useDeclineCertification(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeclineCertificationRequest) =>
      certificationsApi.decline(dealId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
      });
    },
    onError: (err, variables) => {
      if (isConflict(err)) {
        qc.invalidateQueries({
          queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
        });
      }
    },
  });
}

export function useEscalateCertification(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EscalateCertificationRequest) =>
      certificationsApi.escalate(dealId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
      });
      // An analyst-tab escalation creates an EscalationRecord (owner ruling
      // 15, 2026-10-08) — refresh the Escalations queue.
      qc.invalidateQueries({ queryKey: escalationKeys.all });
    },
    onError: (err, variables) => {
      if (isConflict(err)) {
        qc.invalidateQueries({
          queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
        });
      }
    },
  });
}

export function useSaveCertificationState(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveCertificationStateRequest) =>
      certificationsApi.saveDraft(dealId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.certifications(dealId, variables.tab, variables.section),
      });
    },
  });
}

/** Wraps the hand-off cert post + relies on the backend
 *  service to advance Deal.Stage atomically. */
export function useHandOffToClosing(dealId: string) {
  const post = usePostUnderwritingCertification(dealId);
  return useMutation({
    mutationFn: () =>
      post.mutateAsync({
        tab: "Underwriting",
        section: 9,
        certificationCode: "UW.S9.HandOffToClosing",
        label: "I authorize hand-off of this deal to the Closing phase.",
        justification: null,
      }),
  });
}

// ============================== loan doc issuance =====================

export function useLoanDocumentIssuance(dealId: string | undefined) {
  return useQuery<LoanDocumentIssuanceDto | null>({
    queryKey: dealFlowKeys.loanDocumentIssuance(dealId ?? ""),
    queryFn: async () => {
      try {
        return await loanDocumentIssuanceApi.get(dealId!);
      } catch (err) {
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!dealId,
  });
}

export function useUpsertLoanDocumentIssuance(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertLoanDocumentIssuanceRequest) =>
      loanDocumentIssuanceApi.upsert(dealId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: dealFlowKeys.loanDocumentIssuance(dealId) }),
  });
}

// ============================== final financial addendum ==============

export function useFinalFinancialAddendum(dealId: string | undefined) {
  return useQuery<FinalFinancialAddendumDto | null>({
    queryKey: dealFlowKeys.finalFinancialAddendum(dealId ?? ""),
    queryFn: async () => {
      try {
        return await finalFinancialAddendumApi.get(dealId!);
      } catch (err) {
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!dealId,
  });
}

export function useUpsertFinalFinancialAddendum(dealId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertFinalFinancialAddendumRequest) =>
      finalFinancialAddendumApi.upsert(dealId, body),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: dealFlowKeys.finalFinancialAddendum(dealId),
      });
      // The Create Loan preview boards at the addendum's Loan Amount and
      // terms, so it must not quote the figure from before this save.
      qc.invalidateQueries({ queryKey: dealFlowKeys.createLoanPreview(dealId) });
    },
  });
}

// ============================== failures ==============================

export function useFailures(query?: FailureListQuery) {
  return useQuery({
    queryKey: dealFlowKeys.failures(query),
    queryFn: () => failuresApi.list(query),
  });
}

export function useFailure(id: string | undefined) {
  return useQuery({
    queryKey: ["deal-flow", "failure", id ?? ""] as QueryKey,
    queryFn: () => failuresApi.get(id!),
    enabled: !!id,
  });
}

export function useResolveFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveFailureRequest }) =>
      failuresApi.resolve(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "failures"] });
      qc.invalidateQueries({ queryKey: ["deal-flow", "failure", id] });
    },
  });
}

export function useUpdateFailure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateFailureRequest }) =>
      failuresApi.update(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "failures"] });
      qc.invalidateQueries({ queryKey: ["deal-flow", "failure", id] });
    },
  });
}

export function useMarkFailureReviewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string | null }) =>
      failuresApi.markReviewed(id, notes),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["deal-flow", "failures"] });
      qc.invalidateQueries({ queryKey: ["deal-flow", "failure", id] });
    },
  });
}

// ============================== overview tab ===========================

export function useDealFlowOverview(dealId: string | undefined) {
  return useQuery({
    queryKey: ["deal-flow", "overview", dealId ?? ""] as QueryKey,
    queryFn: () => dealFlowOverviewApi.get(dealId!),
    enabled: !!dealId,
    staleTime: 30_000,
  });
}

// ============================== payment setup (Closing Step 2) =========

/** Read-only per-party payment-setup status for the Closing Step 2 surface. */
export function useDealPaymentSetup(dealId: string | undefined) {
  return useQuery<DealPaymentSetupDto>({
    queryKey: dealFlowKeys.paymentSetup(dealId ?? ""),
    queryFn: () => dealPaymentSetupApi.get(dealId!),
    enabled: !!dealId,
  });
}

/** Re-link a party to a portal account (or clear the override with null). */
export function useRelinkDealPartyAccount(dealId: string) {
  const qc = useQueryClient();
  return useMutation<
    DealPaymentSetupPartyDto,
    unknown,
    { party: DealPaymentSetupParty; portalUserId: string | null }
  >({
    mutationFn: ({ party, portalUserId }) =>
      dealPaymentSetupApi.relinkAccount(dealId, party, portalUserId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: dealFlowKeys.paymentSetup(dealId) }),
  });
}

/** Manual "Send reminder" for a party whose setup is incomplete. */
export function useRemindDealParty(dealId: string) {
  const qc = useQueryClient();
  return useMutation<DealPaymentSetupPartyDto, unknown, DealPaymentSetupParty>({
    mutationFn: (party) => dealPaymentSetupApi.remind(dealId, party),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: dealFlowKeys.paymentSetup(dealId) }),
  });
}

// ============== servicing platform (TPS) — Closing Step 2 =============

/** The current staff-driven servicer decision for the deal (drives both the
 *  Step-2 block and the Step-4 soft-gate via useClosingTabState). */
export function useDealTpsAssignment(dealId: string | undefined) {
  return useQuery<DealTpsAssignment>({
    queryKey: dealFlowKeys.tpsAssignment(dealId ?? ""),
    queryFn: () => dealTpsAssignmentApi.get(dealId!),
    enabled: !!dealId,
  });
}

/** Certified TPS firms selectable in the Step-2 picker. */
export function useDealTpsOptions(dealId: string | undefined) {
  return useQuery<DealTpsOption[]>({
    queryKey: dealFlowKeys.tpsAssignmentOptions(dealId ?? ""),
    queryFn: () => dealTpsAssignmentApi.options(dealId!),
    enabled: !!dealId,
  });
}

/** Record (or clear) the servicer decision. Invalidates the assignment query —
 *  which useClosingTabState reads — so the Step-4 gate recomputes immediately. */
export function useSetDealTps(dealId: string) {
  const qc = useQueryClient();
  return useMutation<DealTpsAssignment, unknown, SetDealTpsRequest>({
    mutationFn: (body) => dealTpsAssignmentApi.set(dealId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dealFlowKeys.tpsAssignment(dealId) });
      qc.invalidateQueries({
        queryKey: dealFlowKeys.tpsAssignmentOptions(dealId),
      });
    },
  });
}

// ============================== closing ===============================

/** Step 4 Create-Loan mutation. On success the server flips
 *  the Deal into Closed; invalidate the broad deal cache so all
 *  tabs flip to read-only and surface the Closed banner. */
export function useCreateLoan(dealId: string) {
  const qc = useQueryClient();
  // The mutation variable is the B2-confirmed first-payment amount (undefined =
  // let the server use the computed monthly P&I plus the Payment
  // Administration Fee).
  return useMutation<LoanCreationResultDto, unknown, number | undefined>({
    mutationFn: (firstPaymentAmount) => closingApi.createLoan(dealId, firstPaymentAmount),
    onSuccess: () => {
      invalidateDealCaches(qc, dealId);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}

/** Pre-create preview of the suggested first-payment amount (computed monthly
 *  P&I plus the Payment Administration Fee) + resolved terms, so the
 *  Create-Loan confirm modal can prefill an editable amount. Only fetched once
 *  the step is unlocked.
 *
 *  A refusal (4xx) is not retried: it is the answer Create Loan would give, and
 *  the default retry held the reason back for a second or more after Step 4
 *  unlocked (staging run, 2026-10-15). Other failures keep the one retry. */
export function useCreateLoanPreview(dealId: string, enabled: boolean) {
  return useQuery({
    queryKey: dealFlowKeys.createLoanPreview(dealId),
    queryFn: () => closingApi.createLoanPreview(dealId),
    enabled,
    staleTime: 60_000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
      failureCount < 1,
  });
}
